import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { DialogTrigger, Heading, Text } from 'react-aria-components';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import TextField from 'src/components/ui/TextField';
import Button from 'src/components/ui/Button';
import LinkButton from 'src/components/ui/LinkButton';
import Modal from 'src/components/ui/Modal';
import FeedbackDialog from 'src/components/FeedbackDialog';
import OnboardingPendingPanel, {
  isOnboardingPending,
} from 'src/components/auth/OnboardingPendingPanel';
import { graphQLErrorNotification } from 'src/utils/notifications';
import MailIcon from 'src/assets/icons/envelope.svg';
import ArrowRightIcon from 'src/assets/icons/arrow-right-1.svg';
import MAGIC_LOGIN_MUTATION from 'src/graphql/mutation/magic-login.graphql';
import { ENV_MODE, isWhiteLabel } from 'src/utils';

const CreatorSignInPage = () => {
  useEffect(() => {
    document.body.classList.add('login');

    return () => {
      document.body.classList.remove('login');
    };
  }, []);

  return <SignInForm />;
};

const SignInSchema = z.object({
  email: z
    .string()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address, like name@university.edu.'),
});

type SignInSchemaType = z.infer<typeof SignInSchema>;

const SignInForm = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // The email each panel is about; empty while the form is showing.
  const [noAccountEmail, setNoAccountEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');

  const { handleSubmit, clearErrors, reset, control } =
    useForm<SignInSchemaType>({
      resolver: zodResolver(SignInSchema),
      defaultValues: {
        email: '',
      },
    });

  const [magicLogin, { loading }] = useMutation(MAGIC_LOGIN_MUTATION, {
    // The page decides between a panel and a toast, so the global error
    // toast in errorLink is switched off for this mutation.
    context: { notifyOnError: false },
    onCompleted: async (data, clientOptions) => {
      if (data.magicLogin) {
        const email = clientOptions?.variables?.input?.email;

        await navigate({
          to: '/magic-link',
          replace: true,
          search: { email, from: 'creator-login' },
        });
      }
    },
    onError: (error, clientOptions) => {
      const email = clientOptions?.variables?.input?.email;

      if (isOnboardingPending(error)) {
        setPendingEmail(email);
        return null;
      }

      const errorKeys = (error?.graphQLErrors ?? []).flatMap((ge) =>
        Object.keys(ge?.extensions?.fields ?? {})
      );

      const hasNoAccount = errorKeys.includes(NO_ACCOUNT_ERROR_CODE);

      if (hasNoAccount) {
        setNoAccountEmail(email);
        return null;
      }

      return graphQLErrorNotification(error);
    },
  });

  const onSubmit = useCallback(
    async ({ email = '' }) => {
      const recaptcha = executeRecaptcha
        ? await executeRecaptcha('login')
        : null;
      const input = { email, recaptcha };

      clearErrors();
      await magicLogin({
        variables: {
          input,
          whitelabelKey: isWhiteLabel ? ENV_MODE : null,
          role: 'CREATOR',
        },
      });
      reset();
    },
    [magicLogin, clearErrors, reset, executeRecaptcha]
  );

  if (pendingEmail) {
    return (
      <OnboardingPendingPanel
        email={pendingEmail}
        onBack={() => setPendingEmail('')}
      />
    );
  }

  if (noAccountEmail) {
    return (
      <div>
        <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
          We couldn&apos;t find that account
        </Heading>
        <Text
          elementType="p"
          className="text-sm text-surface-alt-75 font-normal mt-2 mb-8"
        >
          There&apos;s no Creator Studio account for{' '}
          <strong className="text-surface-alt-90">{noAccountEmail}</strong>.
          Check the address for a typo, or request access and our team will
          review it.
        </Text>
        <RequestAccessButton
          className="w-full justify-center"
          email={noAccountEmail}
          onClose={() => setNoAccountEmail('')}
        />
        <Button
          ghost
          variant="surface"
          className="w-full justify-center mt-2"
          onPress={() => setNoAccountEmail('')}
        >
          Try a Different Email
        </Button>
        <StudentSignInLine />
      </div>
    );
  }

  return (
    <div>
      <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
        Welcome to Creator{'\u00A0'}Studio!
      </Heading>
      <Text
        elementType="p"
        className="text-sm text-surface-alt-75 font-normal mt-2 mb-8"
      >
        For faculty, bookstore managers, and administrators. Enter your email to
        sign in.
      </Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              {...field}
              Icon={MailIcon}
              label="Email"
              placeholder="name@university.edu"
              validationBehavior="aria"
              isInvalid={invalid}
              errorMessage={error?.message}
              isRequired
              autoComplete="email"
            />
          )}
        />
        <Button
          className="w-full justify-center mt-8 gap-2"
          type="submit"
          isDisabled={loading}
          loading={loading}
        >
          Continue
          <ArrowRightIcon className="w-3 h-3 text-primary-45" />
        </Button>
      </form>
      <StudentSignInLine />
    </div>
  );
};

// BNC has no student sign-in: `/login` redirects straight back here.
const StudentSignInLine = () =>
  ENV_MODE === 'bnc' ? null : (
    <>
      <span className="block h-px bg-surface-alt-ghost-10 mt-8 mb-5" />
      <p className="text-center text-sm text-surface-alt-75">
        Looking for your course materials?{' '}
        <LinkButton
          href={`/login${window.location.search}`}
          hasArrow={false}
          className="text-sm font-semibold text-surface-alt-90 underline underline-offset-2 decoration-1"
        >
          Student Sign In
        </LinkButton>
      </p>
    </>
  );

export const RequestAccessButton = ({
  className,
  email,
  onClose,
}: {
  className?: string;
  email: string;
  onClose: () => void;
}) => {
  const isMobile = useMediaQuery({
    query: '(max-width: 768px)',
  });

  const size = isMobile ? 'small' : 'medium';

  return (
    <DialogTrigger>
      <Button className={className}>Request Access</Button>
      <Modal className="max-w-[37.5rem]" size={size} isKeyboardDismissDisabled>
        <FeedbackDialog size={size} defaultEmail={email} onClose={onClose} />
      </Modal>
    </DialogTrigger>
  );
};

const NO_ACCOUNT_ERROR_CODE = 'register_creator';

export const Route = createFileRoute('/_auth/creator-login')({
  component: CreatorSignInPage,
});
